import * as yup from 'yup';
import {TFunction} from 'i18next';
import {MedicationPackDto} from '../types/prescription';

const REQUIRED_STRING = 'requiredField';
const MIN_ONE = 'minOne';

export const DOSAGE_FREQUENCY_OPTIONS = ['1x1', '2x1', '3x1', '4x1', 'xH'] as const;
export type DosageFrequencyOption = typeof DOSAGE_FREQUENCY_OPTIONS[number];
export const DOSAGE_MODES = {
    SIMPLIFIED: 'SIMPLIFIED',
    EXTENDED: 'EXTENDED',
} as const;
export type DosageModeOption = typeof DOSAGE_MODES[keyof typeof DOSAGE_MODES];

const hasAnyStructuredDosageValue = (values: {
    treatmentDuration: number | null;
    dosageFrequencyOption: DosageFrequencyOption | '';
    dosageIntervalHours: number | null;
    dosageAmount: number | null;
    dosageUnit: string | null;
}) =>
    values.treatmentDuration != null ||
    Boolean(values.dosageFrequencyOption) ||
    values.dosageIntervalHours != null ||
    values.dosageAmount != null ||
    Boolean(values.dosageUnit);

const hasCompleteStructuredDosageValue = (values: {
    treatmentDuration: number | null;
    dosageFrequencyOption: DosageFrequencyOption | '';
    dosageIntervalHours: number | null;
    dosageAmount: number | null;
    dosageUnit: string | null;
}) =>
    values.treatmentDuration != null &&
    Boolean(values.dosageFrequencyOption) &&
    (values.dosageFrequencyOption !== 'xH' || values.dosageIntervalHours != null) &&
    values.dosageAmount != null &&
    Boolean(values.dosageUnit?.trim());

const getNearestValidDosageIntervals = (
    treatmentDuration: number,
    dosageIntervalHours: number,
) => {
    const totalHours = 24 * treatmentDuration;

    let lower: number | null = null;
    for (let candidate = dosageIntervalHours - 1; candidate >= 1; candidate -= 1) {
        if (totalHours % candidate === 0) {
            lower = candidate;
            break;
        }
    }

    let upper: number | null = null;
    for (let candidate = dosageIntervalHours + 1; candidate <= totalHours; candidate += 1) {
        if (totalHours % candidate === 0) {
            upper = candidate;
            break;
        }
    }

    return {lower, upper};
};

type NewPrescriptionSchemaParams = {
    commonT: TFunction;
    t: TFunction;
    medicationPacks: MedicationPackDto[];
};

const createNewPrescriptionValidationSchema = ({
    commonT,
    t,
    medicationPacks,
}: NewPrescriptionSchemaParams) =>
    yup.object({
        dosageMode: yup
            .string()
            .oneOf(Object.values(DOSAGE_MODES))
            .required(commonT(REQUIRED_STRING)),
        medicinalProduct: yup
            .object()
            .nullable()
            .required(commonT(REQUIRED_STRING)),
        medicationPackId: yup
            .number()
            .nullable()
            .required(commonT(REQUIRED_STRING)),
        effectiveDate: yup.date().required(commonT(REQUIRED_STRING)),
        quantity: yup
            .number()
            .min(1, t(MIN_ONE))
            .required(commonT(REQUIRED_STRING)),
        treatmentDuration: yup
            .number()
            .nullable()
            .transform((value, originalValue) =>
                originalValue === '' || originalValue == null
                    ? null
                    : value,
            )
            .when('dosageMode', {
                is: DOSAGE_MODES.EXTENDED,
                then: schema =>
                    schema
                        .integer(t('integerValueRequired'))
                        .min(1, t(MIN_ONE))
                        .test(
                            'required-when-structured-started',
                            commonT(REQUIRED_STRING),
                            function (value) {
                                if (this.parent.usageRecommendation?.trim()) return true;
                                const anyStructuredFilled = hasAnyStructuredDosageValue({
                                    treatmentDuration: value ?? null,
                                    dosageFrequencyOption: this.parent.dosageFrequencyOption,
                                    dosageIntervalHours: this.parent.dosageIntervalHours ?? null,
                                    dosageAmount: this.parent.dosageAmount ?? null,
                                    dosageUnit: this.parent.dosageUnit?.trim() || null,
                                });

                                if (!anyStructuredFilled) return true;
                                return value != null;
                            },
                        ),
            }),
        dosageFrequencyOption: yup
            .string()
            .nullable()
            .when('dosageMode', {
                is: DOSAGE_MODES.EXTENDED,
                then: schema =>
                    schema
                        .oneOf(['', ...DOSAGE_FREQUENCY_OPTIONS])
                        .test(
                            'required-when-structured-started',
                            commonT(REQUIRED_STRING),
                            function (value) {
                                if (this.parent.usageRecommendation?.trim()) return true;
                                const anyStructuredFilled = hasAnyStructuredDosageValue({
                                    treatmentDuration: this.parent.treatmentDuration ?? null,
                                    dosageFrequencyOption: (value as DosageFrequencyOption | '') || '',
                                    dosageIntervalHours: this.parent.dosageIntervalHours ?? null,
                                    dosageAmount: this.parent.dosageAmount ?? null,
                                    dosageUnit: this.parent.dosageUnit?.trim() || null,
                                });

                                if (!anyStructuredFilled) return true;
                                return Boolean(value);
                            },
                        ),
            }),
        dosageIntervalHours: yup
            .number()
            .nullable()
            .transform((value, originalValue) =>
                originalValue === '' || originalValue == null
                    ? null
                    : value,
            )
            .when('dosageMode', {
                is: DOSAGE_MODES.EXTENDED,
                then: schema =>
                    schema
                        .integer(t('integerValueRequired'))
                        .min(1, t(MIN_ONE))
                        .test(
                            'required-when-hourly-frequency',
                            commonT(REQUIRED_STRING),
                            function (value) {
                                if (this.parent.usageRecommendation?.trim()) return true;
                                const anyStructuredFilled = hasAnyStructuredDosageValue({
                                    treatmentDuration: this.parent.treatmentDuration ?? null,
                                    dosageFrequencyOption: this.parent.dosageFrequencyOption,
                                    dosageIntervalHours: value ?? null,
                                    dosageAmount: this.parent.dosageAmount ?? null,
                                    dosageUnit: this.parent.dosageUnit?.trim() || null,
                                });

                                if (!anyStructuredFilled) return true;
                                if (this.parent.dosageFrequencyOption !== 'xH') return true;
                                return value != null;
                            },
                        )
                        .test(
                            'reimbursed-hourly-frequency-valid',
                            t('invalidDosageIntervalForReimbursedPrescription'),
                            function (value) {
                                const {dosageFrequencyOption, treatmentDuration} = this.parent;
                                if (dosageFrequencyOption !== 'xH') return true;
                                const selectedPack = medicationPacks.find(
                                    pack => pack.id === this.parent.medicationPackId,
                                );
                                if (!selectedPack?.isReimbursed) return true;
                                if (!treatmentDuration || !value) return true;
                                if (((24 * treatmentDuration) % value) === 0) return true;

                                const {lower, upper} = getNearestValidDosageIntervals(
                                    treatmentDuration,
                                    value,
                                );

                                if (lower != null && upper != null) {
                                    return this.createError({
                                        message: t('nearestValidDosageValues', {
                                            x: lower,
                                            y: upper,
                                        }),
                                    });
                                }

                                if (lower != null) {
                                    return this.createError({
                                        message: t('nearestValidDosageValue', {x: lower}),
                                    });
                                }

                                if (upper != null) {
                                    return this.createError({
                                        message: t('nearestValidDosageValue', {x: upper}),
                                    });
                                }

                                return this.createError({
                                    message: t('invalidDosageIntervalForReimbursedPrescription'),
                                });
                            },
                        ),
            }),
        dosageAmount: yup
            .number()
            .nullable()
            .transform((value, originalValue) =>
                originalValue === '' || originalValue == null
                    ? null
                    : value,
            )
            .when('dosageMode', {
                is: DOSAGE_MODES.EXTENDED,
                then: schema =>
                    schema
                        .min(0, t('minZero'))
                        .test(
                            'max-three-decimals',
                            t('maxThreeDecimalPlaces'),
                            value => {
                                if (value == null) return true;
                                return /^\d+(\.\d{1,3})?$/.test(String(value));
                            },
                        )
                        .test(
                            'required-when-structured-started',
                            commonT(REQUIRED_STRING),
                            function (value) {
                                if (this.parent.usageRecommendation?.trim()) return true;
                                const anyStructuredFilled = hasAnyStructuredDosageValue({
                                    treatmentDuration: this.parent.treatmentDuration ?? null,
                                    dosageFrequencyOption: this.parent.dosageFrequencyOption,
                                    dosageIntervalHours: this.parent.dosageIntervalHours ?? null,
                                    dosageAmount: value ?? null,
                                    dosageUnit: this.parent.dosageUnit?.trim() || null,
                                });

                                if (!anyStructuredFilled) return true;
                                return value != null;
                            },
                        ),
            }),
        dosageUnit: yup
            .string()
            .nullable()
            .transform((value, originalValue) =>
                originalValue == null ? null : String(originalValue).trim(),
            )
            .when('dosageMode', {
                is: DOSAGE_MODES.EXTENDED,
                then: schema =>
                    schema.test(
                        'required-when-structured-started',
                        commonT(REQUIRED_STRING),
                        function (value) {
                            if (this.parent.usageRecommendation?.trim()) return true;
                            const anyStructuredFilled = hasAnyStructuredDosageValue({
                                treatmentDuration: this.parent.treatmentDuration ?? null,
                                dosageFrequencyOption: this.parent.dosageFrequencyOption,
                                dosageIntervalHours: this.parent.dosageIntervalHours ?? null,
                                dosageAmount: this.parent.dosageAmount ?? null,
                                dosageUnit: value || null,
                            });

                            if (!anyStructuredFilled) return true;
                            return Boolean(value?.trim());
                        },
                    ),
            }),
        usageRecommendation: yup
            .string()
            .test(
                'usage-required-by-dosage-mode',
                commonT(REQUIRED_STRING),
                function (value) {
                    const normalizedValue = value?.trim() || '';

                    if (this.parent.dosageMode === DOSAGE_MODES.SIMPLIFIED) {
                        return Boolean(normalizedValue);
                    }

                    if (this.parent.dosageMode === DOSAGE_MODES.EXTENDED) {
                        if (Boolean(normalizedValue)) return true;

                        return hasCompleteStructuredDosageValue({
                            treatmentDuration: this.parent.treatmentDuration ?? null,
                            dosageFrequencyOption: this.parent.dosageFrequencyOption || '',
                            dosageIntervalHours: this.parent.dosageIntervalHours ?? null,
                            dosageAmount: this.parent.dosageAmount ?? null,
                            dosageUnit: this.parent.dosageUnit?.trim() || null,
                        });
                    }

                    return true;
                },
            ),
    });

export default createNewPrescriptionValidationSchema;
